import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  ArrowLeft,
  Printer,
  Send,
  Edit,
  Check,
  X,
  Repeat,
  Building2,
  Plane,
  Bus,
  FileText,
  Mail,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Moon,
  Users,
  Star,
  ArrowRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AdminLayout from '../../components/admin/AdminLayout';
import ImageWithFallback from '../../components/ImageWithFallback';
import { GET_QUOTE } from '../../graphql/queries/quote';
import {
  ACCEPT_QUOTE,
  REJECT_QUOTE,
  SEND_QUOTE_BY_CHANNELS,
} from '../../graphql/mutations/quote';

type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CONVERTED';

const statusColors: Record<QuoteStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-yellow-100 text-yellow-800',
  CONVERTED: 'bg-purple-100 text-purple-800',
};

const statusLabels: Record<QuoteStatus, string> = {
  DRAFT: 'Brouillon',
  SENT: 'Envoyé',
  ACCEPTED: 'Accepté',
  REJECTED: 'Refusé',
  EXPIRED: 'Expiré',
  CONVERTED: 'Converti',
};

const typeMeta = {
  HOTEL: { label: 'Hôtel', Icon: Building2, color: 'text-purple-600' },
  FLIGHT: { label: 'Vol', Icon: Plane, color: 'text-blue-600' },
  TRANSFER: { label: 'Transfert', Icon: Bus, color: 'text-orange-600' },
  CUSTOM: { label: 'Prestation', Icon: FileText, color: 'text-gray-600' },
};

function formatCurrency(amount: number, currency = 'DZD') {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('fr-FR')} ${currency}`;
  }
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  try {
    return format(new Date(value), 'dd MMMM yyyy', { locale: fr });
  } catch {
    return value;
  }
}

function formatDateShort(value?: string | null) {
  if (!value) return null;
  try {
    return format(new Date(value), 'dd MMM yyyy', { locale: fr });
  } catch {
    return value;
  }
}

function parseStars(s?: string | null): number {
  if (!s) return 0;
  const m = String(s).match(/(\d)/);
  return m ? Math.max(0, Math.min(5, Number(m[1]))) : 0;
}

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useQuery(GET_QUOTE, {
    variables: { id: Number(id) },
    fetchPolicy: 'cache-and-network',
  });

  const [showSendModal, setShowSendModal] = useState(false);
  const [channel, setChannel] = useState<'email' | 'whatsapp' | 'both'>('email');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [waMessage, setWaMessage] = useState('');
  const [sendResults, setSendResults] = useState<Array<{ channel: string; success: boolean; error?: string | null }>>([]);
  const [sendError, setSendError] = useState('');

  const [sendQuoteByChannels, { loading: sending }] = useMutation(SEND_QUOTE_BY_CHANNELS);
  const [acceptQuote, { loading: accepting }] = useMutation(ACCEPT_QUOTE, {
    onCompleted: () => refetch(),
  });
  const [rejectQuote, { loading: rejecting }] = useMutation(REJECT_QUOTE, {
    onCompleted: () => refetch(),
  });

  if (loading && !data) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-gray-500">Chargement du devis...</div>
      </AdminLayout>
    );
  }

  if (error || !data?.quote) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-red-600">
          Erreur : {error?.message || 'Devis introuvable'}
        </div>
      </AdminLayout>
    );
  }

  const q = data.quote;
  const status = q.status as QuoteStatus;

  const handlePrint = () => {
    // Petite info une seule fois pour aider à obtenir un PDF propre sans
    // URL ni en-tête navigateur.
    try {
      if (!sessionStorage.getItem('printHintShown')) {
        sessionStorage.setItem('printHintShown', '1');
        alert(
          "Astuce : dans la fenêtre d'impression, dépliez « Plus de paramètres » " +
            "et décochez « En-têtes et pieds de page » pour retirer l'URL et le titre."
        );
      }
    } catch {
      /* ignore */
    }
    setTimeout(() => window.print(), 50);
  };

  const openSendModal = () => {
    if (!data?.quote) return;
    const q = data.quote;
    const agencyName = q.agency?.name || 'votre agence';
    setSendResults([]);
    setSendError('');
    setChannel(q.contactEmail ? 'email' : 'whatsapp');
    setEmailSubject(`Votre devis ${q.reference} — ${agencyName}`);
    setEmailMessage(
      `Bonjour ${q.contactPrenom || ''} ${q.contactNom || ''},\n\nVeuillez trouver ci-dessous le devis personnalisé que nous avons préparé pour vous.\n\nN'hésitez pas à nous contacter pour toute question.\n\nCordialement,\n${agencyName}`
    );
    setWaMessage('');
    setShowSendModal(true);
  };

  const handleConfirmSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendError('');
    setSendResults([]);

    const channels: string[] = [];
    if (channel === 'email' || channel === 'both') channels.push('EMAIL');
    if (channel === 'whatsapp' || channel === 'both') channels.push('WHATSAPP');
    if (channels.length === 0) {
      setSendError("Sélectionnez au moins un canal d'envoi.");
      return;
    }

    try {
      const res = await sendQuoteByChannels({
        variables: {
          id: Number(id),
          channels,
          emailSubject: channels.includes('EMAIL') ? emailSubject : null,
          emailMessage: channels.includes('EMAIL') ? emailMessage : null,
          whatsappMessage: channels.includes('WHATSAPP') ? (waMessage || null) : null,
        },
      });
      const results = res.data?.sendQuoteByChannels?.results || [];
      setSendResults(results);
      await refetch();
    } catch (err: any) {
      setSendError(err.message || "Erreur lors de l'envoi.");
    }
  };

  const handleAccept = async () => {
    if (!confirm('Marquer ce devis comme accepté par le client ?')) return;
    try {
      await acceptQuote({ variables: { id: Number(id) } });
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleReject = async () => {
    if (!confirm('Marquer ce devis comme refusé par le client ?')) return;
    try {
      await rejectQuote({ variables: { id: Number(id) } });
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleConvert = () => {
    navigate(`/admin/quotes/${id}/convert`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 print:space-y-3">
        {/* Toolbar (caché en print) */}
        <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/quotes')}
              className="text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Devis {q.reference}</h1>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[status]}`}
              >
                {statusLabels[status]}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ActionBtn icon={Printer} label="Imprimer / PDF" onClick={handlePrint} />
            {status === 'DRAFT' && (
              <ActionBtn
                icon={Edit}
                label="Modifier"
                onClick={() => navigate(`/admin/quotes/${id}/edit`)}
              />
            )}
            {(status === 'DRAFT' || status === 'SENT') && (
              <ActionBtn
                icon={Send}
                label="Envoyer au client"
                variant="primary"
                onClick={openSendModal}
                disabled={sending}
              />
            )}
            {status === 'SENT' && (
              <>
                <ActionBtn
                  icon={Check}
                  label="Marquer accepté"
                  variant="success"
                  onClick={handleAccept}
                  disabled={accepting}
                />
                <ActionBtn
                  icon={X}
                  label="Marquer refusé"
                  variant="danger"
                  onClick={handleReject}
                  disabled={rejecting}
                />
              </>
            )}
            {status === 'ACCEPTED' && (
              <ActionBtn
                icon={Repeat}
                label="Convertir en réservation"
                variant="primary"
                onClick={handleConvert}
              />
            )}
          </div>
        </div>

        {/* Document (pour le print) */}
        <div
          id="quote-print-area"
          className="bg-white rounded-lg shadow p-8 max-w-4xl mx-auto print:shadow-none print:p-0 print:max-w-none print:mx-0"
        >
          {/* En-tête */}
          <div className="flex items-start justify-between gap-6 mb-8 pb-6 border-b-2 border-blue-600">
            <div className="flex items-center gap-4">
              <ImageWithFallback
                src={q.agency?.logo}
                alt={q.agency?.name || ''}
                className="h-20 w-20 object-contain rounded bg-white"
                fallback={null}
              />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{q.agency?.name}</h2>
              </div>
            </div>
            <div className="text-right">
              <h1 className="text-4xl font-bold text-blue-600 mb-1 tracking-wide">DEVIS</h1>
              <div className="text-sm text-gray-600 space-y-0.5">
                <div className="font-mono font-semibold text-gray-900">{q.reference}</div>
                <div>Émis le {formatDate(q.createdAt)}</div>
                <div>Valable jusqu'au <strong>{formatDate(q.validUntil)}</strong></div>
              </div>
            </div>
          </div>

          {/* Client + voyage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Client
              </h3>
              <div className="text-sm text-gray-900">
                <div className="font-semibold">
                  {q.contactPrenom} {q.contactNom}
                </div>
                <div>{q.contactEmail}</div>
                <div>{q.contactTelephone}</div>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Voyage
              </h3>
              <div className="text-sm text-gray-900">
                <div className="font-semibold">{q.title}</div>
                {q.destination && <div>Destination : {q.destination}</div>}
                {q.travelDateStart && (
                  <div>
                    Du {formatDate(q.travelDateStart)}
                    {q.travelDateEnd && ` au ${formatDate(q.travelDateEnd)}`}
                  </div>
                )}
                <div>
                  {q.nbrAdulte} adulte{q.nbrAdulte > 1 ? 's' : ''}
                  {q.nbrEnfant > 0 && `, ${q.nbrEnfant} enfant${q.nbrEnfant > 1 ? 's' : ''}`}
                  {q.nbrBebe > 0 && `, ${q.nbrBebe} bébé${q.nbrBebe > 1 ? 's' : ''}`}
                </div>
              </div>
            </div>
          </div>

          {q.description && (
            <div className="mb-8">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Description
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-line">{q.description}</p>
            </div>
          )}

          {/* Prestations */}
          <div className="mb-8">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
              Prestations
            </h3>
            <div className="space-y-4">
              {q.lineItems.map((it: any) => {
                if (it.type === 'FLIGHT') return <FlightCard key={it.id} item={it} currency={q.currency} />;
                if (it.type === 'HOTEL') return <HotelCard key={it.id} item={it} currency={q.currency} />;
                return <SimpleCard key={it.id} item={it} currency={q.currency} />;
              })}
            </div>
          </div>

          {/* Totaux */}
          <div className="flex justify-end mb-8">
            <div className="w-full md:w-1/2 space-y-1 text-sm">
              <Row label="Sous-total" value={q.subtotal} currency={q.currency} />
              {q.discountPercent > 0 && (
                <Row
                  label={`Remise (${q.discountPercent}%)`}
                  value={-q.discountAmount}
                  currency={q.currency}
                  negative
                />
              )}
              {q.taxPercent > 0 && (
                <Row label={`Taxes (${q.taxPercent}%)`} value={q.taxAmount} currency={q.currency} />
              )}
              <div className="border-t border-gray-300 pt-2 mt-2">
                <Row
                  label="TOTAL"
                  value={q.totalPrice}
                  currency={q.currency}
                  bold
                />
              </div>
            </div>
          </div>

          {q.notes && (
            <div className="text-xs text-gray-500 border-t pt-4 print:hidden">
              <strong>Notes internes :</strong> {q.notes}
            </div>
          )}

          <div className="mt-8 pt-6 border-t text-xs text-gray-500 text-center">
            Devis valable jusqu'au <strong>{formatDate(q.validUntil)}</strong>. Au-delà, les
            tarifs et disponibilités sont susceptibles d'évoluer.
          </div>
        </div>
      </div>

      {/* Modal d'envoi (style harmonisé avec SendOfferModal de la partie clients) */}
      {showSendModal && (() => {
        const hasEmail = Boolean(q.contactEmail);
        const hasPhone = Boolean(q.contactTelephone);
        const needsEmail = channel === 'email' || channel === 'both';
        const needsWa = channel === 'whatsapp' || channel === 'both';
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print:hidden">
            <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Envoyer le devis</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    à <span className="font-medium text-gray-700">{q.contactPrenom} {q.contactNom}</span>
                    {' '}— <span className="font-mono">{q.reference}</span>
                  </p>
                </div>
                <button onClick={() => setShowSendModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleConfirmSend} className="flex-1 overflow-y-auto p-5 space-y-5">
                {sendResults.length > 0 && (
                  <div className="space-y-1.5">
                    {sendResults.map((r, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${
                          r.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {r.success
                          ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                          : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                        <span>
                          {r.channel === 'EMAIL' ? 'Email' : 'WhatsApp'} :{' '}
                          {r.success ? 'Envoyé' : (r.error || 'Échec')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {sendError && (
                  <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {sendError}
                  </div>
                )}

                {/* Canal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Canal d'envoi</label>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { value: 'email',    label: 'Email',    icon: <Mail className="w-4 h-4" />,          disabled: !hasEmail },
                      { value: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare className="w-4 h-4" />, disabled: !hasPhone },
                      { value: 'both',     label: 'Les deux', icon: <Send className="w-4 h-4" />,          disabled: !hasEmail || !hasPhone },
                    ] as { value: 'email' | 'whatsapp' | 'both'; label: string; icon: React.ReactNode; disabled: boolean }[]).map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={opt.disabled}
                        onClick={() => setChannel(opt.value)}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          channel === opt.value ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-700 hover:border-blue-400'
                        }`}
                      >
                        {opt.icon} {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Email */}
                {needsEmail && (
                  <div className="space-y-3 border border-blue-100 bg-blue-50/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                      <Mail className="w-4 h-4" />
                      Email — {q.contactEmail}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Objet</label>
                      <input
                        value={emailSubject}
                        onChange={e => setEmailSubject(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Message d'accompagnement</label>
                      <textarea
                        value={emailMessage}
                        onChange={e => setEmailMessage(e.target.value)}
                        rows={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">Le récapitulatif détaillé du devis sera ajouté automatiquement sous votre message.</p>
                    </div>
                  </div>
                )}

                {/* WhatsApp */}
                {needsWa && (
                  <div className="space-y-3 border border-green-100 bg-green-50/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                      <MessageSquare className="w-4 h-4" />
                      WhatsApp — {q.contactTelephone}
                    </div>
                    <textarea
                      value={waMessage}
                      onChange={e => setWaMessage(e.target.value)}
                      rows={4}
                      placeholder="Message personnalisé (optionnel — le résumé du devis est envoyé automatiquement si vide)..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                )}
              </form>

              {/* Footer */}
              <div className="flex gap-3 p-5 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowSendModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmSend}
                  disabled={sending || (!hasEmail && !hasPhone)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending
                    ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Envoi...</>
                    : <><Send className="w-4 h-4" /> Envoyer le devis</>
                  }
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </AdminLayout>
  );
}

function Row({
  label,
  value,
  currency,
  negative,
  bold,
}: {
  label: string;
  value: number;
  currency: string;
  negative?: boolean;
  bold?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between ${bold ? 'text-base font-bold' : ''}`}>
      <span className={negative ? 'text-red-600' : ''}>{label}</span>
      <span className={negative ? 'text-red-600' : ''}>{formatCurrency(value, currency)}</span>
    </div>
  );
}

// ─── Cartes pour les prestations (style "carnet de voyage") ────────────────

function FlightCard({ item, currency }: { item: any; currency: string }) {
  const from = item.flightFrom || 'Ville de départ';
  const to = item.flightTo || "Ville d'arrivée";
  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 print:border-gray-300">
      {/* Bandeau bleu */}
      <div className="bg-blue-600 text-white px-5 py-3 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <Plane className="w-5 h-5 mt-1" />
          <div>
            <div className="text-base font-bold tracking-wide uppercase">Vol</div>
            <div className="text-sm text-blue-100">
              {from} - {to}
            </div>
          </div>
        </div>
        {item.airline && (
          <div className="text-xs text-blue-100 text-right">
            {item.airline}
            {item.flightNumber ? ` · ${item.flightNumber}` : ''}
          </div>
        )}
      </div>

      {/* Corps */}
      <div className="bg-white px-5 py-4">
        <div className="grid grid-cols-3 gap-2 items-center">
          {/* Départ */}
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase mb-1">
              <Plane className="w-3.5 h-3.5" />
              <span>Départ</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatDateShort(item.departureDate) || '—'}
            </div>
          </div>

          {/* Milieu */}
          <div className="text-center">
            <div className="text-sm font-medium text-gray-700">Direct</div>
            <div className="flex items-center justify-center gap-1 my-1">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              <ArrowRight className="w-3 h-3 text-gray-400" />
              <span className="w-2 h-2 rounded-full bg-gray-400" />
            </div>
            <div className="text-xs text-gray-500">
              {item.flightClass || 'Économique'}
            </div>
          </div>

          {/* Retour / Arrivée */}
          <div className="text-right">
            <div className="flex items-center gap-1.5 justify-end text-xs text-gray-500 font-medium uppercase mb-1">
              <Plane className="w-3.5 h-3.5 -scale-x-100" />
              <span>{item.returnDate ? 'Retour' : 'Arrivée'}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatDateShort(item.returnDate) || formatDateShort(item.departureDate) || '—'}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <div className="text-sm text-gray-700">
            <span className="text-gray-500">Classe : </span>
            <span className="font-medium">{item.flightClass || 'Économique'}</span>
            {item.quantity > 1 && (
              <span className="ml-3 text-gray-500">× {item.quantity}</span>
            )}
          </div>
          <div className="text-xl font-bold text-blue-600">
            {formatCurrency(item.total, currency)}
          </div>
        </div>
      </div>
    </div>
  );
}

function HotelCard({ item, currency }: { item: any; currency: string }) {
  const stars = parseStars(item.hotelStars);
  const imageUrl =
    item.providerData?.image ||
    item.providerData?.imageUrl ||
    null;

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 print:border-gray-300 bg-white">
      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <div className="md:w-56 md:flex-shrink-0 bg-gray-100 flex items-center justify-center md:min-h-[200px]">
          {imageUrl ? (
            <img src={imageUrl} alt={item.hotelName || item.title} className="w-full h-full object-cover" />
          ) : (
            <Building2 className="w-16 h-16 text-gray-300" />
          )}
        </div>

        {/* Détails */}
        <div className="flex-1 p-5 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Hébergement
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">
              {item.hotelName || item.title}
            </div>
            {stars > 0 && (
              <div className="flex items-center gap-0.5 mt-1">
                {Array.from({ length: 5 }).map((_, i) =>
                  i < stars ? (
                    <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />
                  ) : (
                    <Star key={i} className="w-4 h-4 text-gray-300" />
                  )
                )}
              </div>
            )}
            {item.hotelLocation && (
              <div className="text-sm text-gray-600 mt-1">{item.hotelLocation}</div>
            )}
          </div>

          {(item.checkIn || item.checkOut) && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Moon className="w-4 h-4 text-gray-500" />
              <span>
                {formatDateShort(item.checkIn)}
                {item.checkOut ? ` - ${formatDateShort(item.checkOut)}` : ''}
                {item.numberOfNights ? ` (${item.numberOfNights} Nuit${item.numberOfNights > 1 ? 's' : ''})` : ''}
              </span>
            </div>
          )}

          {(item.numberOfRooms || item.roomType) && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Users className="w-4 h-4 text-gray-500" />
              <span>
                {item.numberOfRooms ? `${item.numberOfRooms} x ` : ''}
                {item.roomType || 'Chambre'}
              </span>
            </div>
          )}

          {item.mealPlan && (
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
              </span>
              <span className="text-gray-700">{item.mealPlan}</span>
            </div>
          )}

          {item.description && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <AlertCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span>{item.description}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-100">
            <div className="text-sm text-gray-700">
              {item.quantity > 1 && (
                <span className="text-gray-500 mr-2">× {item.quantity}</span>
              )}
              <span className="text-xs text-gray-400">PU {formatCurrency(item.unitPrice, currency)}</span>
            </div>
            <div className="text-xl font-bold text-blue-600">
              {formatCurrency(item.total, currency)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SimpleCard({ item, currency }: { item: any; currency: string }) {
  const meta = typeMeta[item.type as keyof typeof typeMeta] || typeMeta.CUSTOM;
  const Icon = meta.Icon;
  return (
    <div className="rounded-xl border border-gray-200 print:border-gray-300 bg-white p-5">
      <div className="flex items-start gap-3">
        <div className={`${meta.color} mt-0.5`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className={`text-xs font-semibold uppercase tracking-wider ${meta.color}`}>
            {meta.label}
          </div>
          <div className="text-base font-semibold text-gray-900 mt-0.5">
            {item.title}
          </div>
          {item.type === 'TRANSFER' && (
            <div className="text-sm text-gray-600 mt-1 space-y-0.5">
              {(item.transferFrom || item.transferTo) && (
                <div>
                  {item.transferFrom || '—'} → {item.transferTo || '—'}
                </div>
              )}
              {item.transferDate && <div>{formatDateShort(item.transferDate)}</div>}
              {item.vehicleType && <div>{item.vehicleType}</div>}
            </div>
          )}
          {item.description && item.type !== 'TRANSFER' && (
            <div className="text-sm text-gray-600 mt-1 whitespace-pre-line">
              {item.description}
            </div>
          )}
        </div>
        <div className="text-right">
          {item.quantity > 1 && (
            <div className="text-xs text-gray-500 mb-0.5">× {item.quantity}</div>
          )}
          <div className="text-lg font-bold text-blue-600">
            {formatCurrency(item.total, currency)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({
  icon: Icon,
  label,
  onClick,
  variant = 'default',
  disabled,
}: {
  icon: any;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'primary' | 'success' | 'danger';
  disabled?: boolean;
}) {
  const styles: Record<string, string> = {
    default: 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
    primary: 'bg-blue-600 hover:bg-blue-700 text-white border border-transparent',
    success: 'bg-green-600 hover:bg-green-700 text-white border border-transparent',
    danger: 'bg-red-600 hover:bg-red-700 text-white border border-transparent',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50 ${styles[variant]}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
